import type LineItem from '~/types/LineItem';
import type Order from '~/types/Order';
import type Shipment from '~/types/Shipment';

import { type OrderShipmentV2 } from '~/modules/orders/types/ordersV2.type';
import { type OrderSelection } from '~/types/Order';
import {
  calculateValueByQuantity,
  isFulfilled,
  isFulfilledInShippoOldExperience,
  isOrderLineItemEditable,
  isPartialFulfillmentAllCompleted,
  isPartiallyFulfilled,
} from '~/types/Order';
import { type BatchEntryExtended } from '~/types/OrderBatchEntry';
import { BatchEntryStatus } from '~/types/OrdersBatch';
import { TransactionStatus } from '~/types/Transaction';

export const getHasNoRates = (shipment: Shipment | null | undefined) =>
  (shipment?.rates || []).length === 0;

export const getHasFoundNoRates = ({
  orderShipmentStatus,
  shipment,
}: {
  orderShipmentStatus?: OrderShipmentV2['status']['lifecycle_status'];
  shipment: Shipment | null | undefined;
}) => {
  if (!orderShipmentStatus) return false;
  if (orderShipmentStatus === BatchEntryStatus.RATE_NOT_AVAILABLE) return true;
  if (orderShipmentStatus !== BatchEntryStatus.COMPLETE_SHIPMENT) return false;

  if (!shipment) return false;
  if (!getHasNoRates(shipment)) return false;
  if (shipment.messages.length === 0) return false;

  return true;
};

export const isRowEntryFulfilled = (
  order: Order,
  partialParentRow: boolean,
  batchEntry?: BatchEntryExtended,
) => {
  const hasCompleteBatchEntry =
    batchEntry && batchEntry.objectStatus === BatchEntryStatus.COMPLETE_TRANSACTION;
  if (hasCompleteBatchEntry && (isFulfilled(order) || isPartiallyFulfilled(order))) return true;

  const isPartialParentFulfilled = partialParentRow && isPartialFulfillmentAllCompleted(order);
  if (isPartialParentFulfilled) return true;

  if (isFulfilledInShippoOldExperience(order)) return true;

  return false;
};

export const getSelectedOrdersWithLabels = (
  selectedOrders: OrderSelection,
  ordersData: { [orderId: string]: Order },
  batchEntries: HashMap<number, BatchEntryExtended>,
) =>
  Object.entries(selectedOrders).reduce<OrderSelection>((obj, [orderId, batchEntryIds]) => {
    if (batchEntryIds.length > 0) {
      const beIdsWithTransaction = batchEntryIds.filter(
        (beId) => !!batchEntries[beId]?.transactionObjectId,
      );

      // No batch entries have transactions, ignore any possible transactions in order
      if (beIdsWithTransaction.length === 0) return obj;

      return { ...obj, [orderId]: beIdsWithTransaction };
    }

    // No batch entries but has transaction
    if (ordersData[orderId]?.transactions.length > 0) return { ...obj, [orderId]: [] };

    return obj;
  }, {});

/**
 * This method is like the previous method but this
 * goes one level deeper and check if the transaction
 * actually has a label and not a failed transaction
 * @param selectedOrders
 * @param ordersData
 * @param batchEntries
 * @returns
 */
export const getSelectedOrdersWithSuccessfulTransaction = (
  selectedOrders: OrderSelection,
  ordersData: { [orderId: string]: Order },
  batchEntries: HashMap<number, BatchEntryExtended>,
): OrderSelection =>
  Object.entries(selectedOrders).reduce<OrderSelection>((obj, [orderId, batchEntryIds]) => {
    if (batchEntryIds.length > 0) {
      const beIdsWithTransaction = batchEntryIds.filter((beId) => {
        const transactionObjectID = batchEntries[beId]?.transactionObjectId;
        const transaction = ordersData[orderId]?.transactions.find(
          (t) => t.objectId === transactionObjectID,
        );
        return transaction?.status === TransactionStatus.SUCCESS;
      });

      // No batch entries have transactions, ignore any possible transactions in order
      if (beIdsWithTransaction.length === 0) return obj;

      return { ...obj, [orderId]: beIdsWithTransaction };
    }

    // No batch entries but has transaction
    if (
      ordersData[orderId]?.transactions.filter((t) => t.status === TransactionStatus.SUCCESS)
        .length > 0
    )
      return { ...obj, [orderId]: [] };

    return obj;
  }, {});

export const lineItemsFromBatchEntry = (
  order: Order | undefined,
  batchEntry?: BatchEntryExtended,
): LineItem[] => {
  if (!order) return [];
  if (!batchEntry || batchEntry.lineItems.length === 0) return order.lineItems;

  const orderLineItemMap = order.lineItems.reduce<Record<string, LineItem>>((accum, li) => {
    accum[li.objectId] = li;
    return accum;
  }, {});

  return batchEntry.lineItems
    .filter((lineItem) => orderLineItemMap[lineItem.objectId])
    .reduce((list: LineItem[], lineItemLite) => {
      const lineItem = orderLineItemMap[lineItemLite.objectId];
      const { quantity } = lineItemLite;

      list.push({
        ...lineItem,
        quantity,
        totalAmount: calculateValueByQuantity(lineItem.unitAmount, quantity),
        totalWeight: calculateValueByQuantity(lineItem.unitWeight, quantity),
      });

      return list;
    }, []);
};

export const editItemsLinkTarget = (order?: Order) => {
  if (!order || !isOrderLineItemEditable(order)) return;

  return `/orders/${order.objectId}`;
};
